import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import {
  GQL_GET_BATTLE_QUESTION,
  GQL_VALIDATE_BATTLE_ANSWER,
} from '~/gql/battles/battles';
import { authClient } from '~/helpers/apollo';
import { StoredTheme } from '~/gql/theme/theme';
import { StoredAnswer } from '~/gql/answers/answers';
import styled from 'styled-components';
import { QcuItem } from '../ui/qcu-item';
import { SSpanTextLg, SSpanTitle, SSpanTitleSmall } from '../styles/styles';
import { classic } from '~/themes';
import { formatText, renderFullPath } from '~/utils/common/utils';
import { StoredQuestion } from '~/gql/questions/questions';
import { TimerItem } from '~/components/ui/timer-item';
import { ButtonNext } from '~/components/ui/button-next';
import { useLingui } from '@lingui/react';
import { useTimer } from '~/helpers/hooks/timer';
import { timeAnswer } from '~/data/constantes';
import iconBattle from '@assets/images/background/mode-battle.svg';
import quizImg from '@assets/images/background/quiz-background.png';
import useDebouncedCallback from '~/helpers/hooks/debounce';

type Props = {
  themeId: string | undefined;
  theme: StoredTheme | undefined;
  crownCount: number;
  isChallenger: boolean;
  isBonusCrown: boolean;
  battlePlayerTrackingId: string;
  setTurnEnded: React.Dispatch<React.SetStateAction<boolean>>;
  setThemeId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setTheme: React.Dispatch<React.SetStateAction<StoredTheme | undefined>>;
  onFinish: (
    deltaCrown: number,
    newGameScore:
      | {
          crownCount: number;
          badges: string[];
          turn: number;
        }
      | undefined
  ) => void;
};

const BattleQuestion = ({
  themeId,
  battlePlayerTrackingId,
  isBonusCrown,
  isChallenger,
  setTheme,
  setThemeId,
  setTurnEnded,
  crownCount,
  onFinish,
}: Props) => {
  const { i18n } = useLingui();
  const { time, start, clear, stop } = useTimer();
  const [selectedAnswerId, setSelectedAnswerId] = useState<string>('');
  const [correctAnswerId, setCorrectAnswerId] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [question, setQuestion] = useState<StoredQuestion>();
  const [feedback, setFeedback] = useState<boolean>(false);
  const [turn, setTurn] = useState<boolean>(false);
  const [feedbackStatus, setFeedbackStatus] = useState<
    'good' | 'wrong' | 'timeout' | undefined
  >(undefined);
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [newCrownCount, setNewCrownCount] = useState<number | undefined>(
    undefined
  );
  const [newGameScore, setNewGameScore] = useState<
    | {
        crownCount: number;
        badges: string[];
        turn: number;
      }
    | undefined
  >(undefined);
  const [skipQuery, setSkipQuery] = useState<boolean>(true);

  const { refetch: fetchQuestion } = useQuery(GQL_GET_BATTLE_QUESTION, {
    client: authClient,
    initialFetchPolicy: 'network-only',
    skip: skipQuery,
    variables: {
      themeId: themeId ?? '',
      battlePlayerTrackingId,
    },
    onCompleted: (data) => {
      clear();
      setSelectedAnswerId('');
      setCorrectAnswerId('');
      setNewGameScore(undefined);
      setFeedback(false);
      setSubmitted(false);
      setTurn(false);
      setQuestion(data.getBattleQuestion);
      start();
    },
  });

  const [validateBattleAnswer] = useMutation(GQL_VALIDATE_BATTLE_ANSWER, {
    client: authClient,
  });

  useEffect(() => {
    if (time < timeAnswer) {
      return;
    }
    handleValidate();
  }, [time]);

  useEffect(() => {
    setSkipQuery(false);
    void fetchQuestion();
    return () => setSkipQuery(true);
  }, []);

  useEffect(() => {
    if (selectedAnswerId) {
      setTimeout(() => {
        handleValidate();
      }, 500);
    }
  }, [selectedAnswerId]);

  async function handleValidate() {
    stop();
    try {
      const { data } = await validateBattleAnswer({
        variables: {
          answerId: selectedAnswerId ?? '',
          isChallenger,
          isBonusCrown,
          battlePlayerTrackingId,
          questionId: question?.id ?? '',
          themeId: themeId ?? '',
        },
      });
      if (data) {
        if (time >= timeAnswer) {
          setFeedbackStatus('timeout');
        } else {
          data.validateBattleAnswer.correctAnswerId === selectedAnswerId
            ? setFeedbackStatus('good')
            : setFeedbackStatus('wrong');
        }
        setNewCrownCount(data.validateBattleAnswer.crownCount);
        setNewGameScore({
          crownCount: data.validateBattleAnswer.crownCount,
          badges: data.validateBattleAnswer.badges,
          turn: data.validateBattleAnswer.currentTurn,
        });
        setCorrectAnswerId(data.validateBattleAnswer.correctAnswerId);
        setFeedbackText(
          getFeedbackText(data.validateBattleAnswer.correctAnswerId)
        );
        setFeedback(true);
        setSubmitted(true);
        setTurn(data.validateBattleAnswer.turnEnded);
        //================
      }
    } catch {}
  }

  function getFeedbackText(correctAnswer: string): string {
    if (time >= timeAnswer) return 'Temps écoulé...';
    if (correctAnswer === selectedAnswerId && time <= timeAnswer) {
      return 'Correct';
    } else {
      return 'Incorrect';
    }
  }

  function handleNextQuestion() {
    setThemeId(undefined);
    setTheme(undefined);
    setQuestion(undefined);
    setTurnEnded(turn);
    onFinish(newCrownCount ? newCrownCount - crownCount : 0, newGameScore);
  }

  const debouncedHandleClick = useDebouncedCallback(handleNextQuestion, 300);

  return (
    <Container $background={quizImg}>
      {question && (
        <SDivWrapperContent>
          <SDivBattleLogo>
            <img src={iconBattle} alt="" />
          </SDivBattleLogo>
          <SDivContent>
            <SDivQuestion>
              <SDivQuestionText>
                <SSpanTextLgCustom
                  $color={classic.token?.colorWhite}
                  $align="center"
                >
                  {formatText(question.text)}
                </SSpanTextLgCustom>
              </SDivQuestionText>
              <SDivQuestionImg>
                <SQuestionImg
                  src={renderFullPath(question.picture.fileName)}
                  alt={question.picture.fileName}
                />
              </SDivQuestionImg>
              <SDivQuestionText>
                <SSpanTitleSmall
                  $marginBottom="0"
                  $color={classic.token?.colorWhite}
                  $bold
                >
                  {question.theme?.name}
                </SSpanTitleSmall>
              </SDivQuestionText>
            </SDivQuestion>
            <SDivAnswers>
              {question.answers.map((answer: StoredAnswer) => (
                <SAnswerWrapper key={answer.id}>
                  <SAnswer>
                    <QcuItem
                      submitted={submitted}
                      onClick={() => {
                        if (!selectedAnswerId) {
                          stop();
                          setSelectedAnswerId(answer.id);
                        }
                      }}
                      text={answer.text}
                      selected={selectedAnswerId === answer.id}
                      status={
                        !feedback
                          ? 'neutral'
                          : correctAnswerId === answer.id
                            ? 'good'
                            : 'wrong'
                      }
                    />
                  </SAnswer>
                </SAnswerWrapper>
              ))}
            </SDivAnswers>
          </SDivContent>
          <TimerItem timeLeft={timeAnswer - time}></TimerItem>
          <ButtonNext disabled={!feedback} onClick={debouncedHandleClick}>
            {i18n._('common.next')}
          </ButtonNext>
        </SDivWrapperContent>
      )}
      {/* <Button color="primary" onClick={() => validateBattleAnswer()}>
        Répondre
      </Button> */}
      {feedback && (
        <SDivFeedback>
          <SDivFeedbackText $status={feedbackStatus}>
            <SSpanTitle $color={classic.token?.colorWhite}>
              {feedbackText}
            </SSpanTitle>
          </SDivFeedbackText>
        </SDivFeedback>
      )}
    </Container>
  );
};

export default BattleQuestion;

const SSpanTextLgCustom = styled(SSpanTextLg)`
  line-height: normal;
  font-weight: 400;
  font-size: 1.35rem;
  font-family: 'Fira sans', serif;
  margin-bottom: 0;

  margin-top: 10px;
  @media screen and (max-width: 800px) {
    font-size: 1.2rem;
    margin-top: 10px;
  }

  @media screen and (max-height: 450px) {
    font-size: 1rem;
    margin-top: 3px;
  }
  @media screen and (max-width: 450px) {
    font-size: 1rem;
    margin-top: 7px;
  }
`;

const SDivBattleLogo = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 10px;
  pointer-events: none;
  user-select: none;
  height: 60px;
  width: 50%;
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  @media screen and (max-height: 415px) {
    height: 40px;
  }
  @media screen and (max-width: 450px) {
    height: 40px;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
`;

const SDivFeedback = styled.div<{ $status?: 'good' | 'wrong' }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const Container = styled.div<{ $background?: string }>`
  background-image: ${({ $background }) =>
    $background ? `url('${$background}')` : 'none'};
  background-size: cover;
  align-items: center;
  mix-blend-mode: color;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  width: 100%;
  padding: 60px 80px;

  @media screen and (max-width: 800px) {
    padding: 60px 60px;
  }

  @media screen and (max-height: 450px) {
    padding: 50px 30px;
  }
  @media screen and (max-width: 450px) {
    padding: 50px 10px;
  }
`;

const SDivWrapperContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 90%;
  width: 95%;
  position: relative;
  @media screen and (max-width: 800px) {
    width: 100%;
  }
  @media screen and (max-height: 450px) {
    width: 100%;
  }
  @media screen and (max-width: 450px) {
    width: 100%;
  }
`;

const SDivContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  border-radius: 40px;
  border-right: 1px solid #fff;
  border-bottom: 3px solid #fff;
  border-left: 1px solid #fff;
  background: rgba(255, 255, 255, 0.3);
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const SDivQuestion = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 0 0 55%;
  padding: 40px;
  height: 100%;
  @media screen and (max-height: 450px) {
    padding: 10px;
  }
  @media screen and (max-width: 450px) {
    padding: 10px;
  }
`;

const SDivQuestionImg = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 50%;
  width: 100%;
  @media screen and (max-width: 450px) {
    height: 30%;
  }
`;

const SQuestionImg = styled.img`
  border-radius: 16px;
  object-fit: contain;
  height: 85%;
  width: 85%;

  @media screen and (max-width: 800px) {
    height: 85%;
    width: 80%;
  }
  @media screen and (max-height: 450px) {
    height: 95%;
    width: 95%;
  }
  @media screen and (max-width: 450px) {
    height: 95%;
    width: 95%;
  }

  border: 1px solid #ffffff80;
`;

const SDivQuestionText = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: 50%;
  width: 95%;

  @media screen and (max-width: 450px) {
    height: 70%;
  }
`;

const SDivAnswers = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex: 0 0 45%;
  height: 100%;
  padding: 40px 40px 40px 0;
  @media screen and (max-height: 450px) {
    padding: 10px 10px 10px 0;
  }
  @media screen and (max-width: 450px) {
    padding: 10px 10px 10px 0;
  }
`;

const SAnswerWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 23%;
  width: 100%;
  @media screen and (max-width: 450px) {
    height: 20%;
  }
`;

const SAnswer = styled.div`
  height: 100%;
  width: 100%;
`;

export const SDivFeedbackText = styled.div<{
  $status?: 'good' | 'wrong' | 'timeout';
}>`
  align-items: flex-start;
  display: flex;
  justify-content: center;
  background: ${({ $status }) => {
    if ($status === 'good')
      return 'linear-gradient(0deg, rgba(45, 191, 112, 0.00) 0%, #2DBF70 100%)';
    if ($status === 'wrong')
      return 'linear-gradient(180deg, #CC3E58 0%, rgba(204, 62, 88, 0.00) 100%)';
    if ($status === 'timeout')
      return 'linear-gradient(180deg, #B6B6B6 0%, rgba(182, 182, 182, 0.00) 100%)';
  }};
  padding-top: 20px;
  position: absolute;
  top: 0;
  left: 0;
  height: 30%;
  width: 100%;
`;
